import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('ready'); // ready | success

  // Small UX guard: if token missing, show nice error UI below
  useEffect(() => {
    setStatus('ready');
  }, [token]);

  const rules = useMemo(() => {
    const hasMinLen = password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const match = password.length > 0 && password === confirmPassword;

    return { hasMinLen, hasLetter, hasNumber, match };
  }, [password, confirmPassword]);

  const canSubmit =
    token &&
    !loading &&
    rules.hasMinLen &&
    rules.hasLetter &&
    rules.hasNumber &&
    password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error('Reset link is missing a token.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Slightly stronger than before (8 chars + letter + number)
    if (!rules.hasMinLen || !rules.hasLetter || !rules.hasNumber) {
      toast.error('Use at least 8 characters with letters and numbers.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });

      setStatus('success');
      toast.success('Password reset successfully');

      // redirect after a short moment
      setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Reset link is invalid or expired';

      // Helpful UX: if token expired/invalid, keep them in a "try again" state
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ ok, text }) => (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
      ) : (
        <XCircle className="w-4 h-4 text-gray-300" />
      )}
      <span className={ok ? 'text-emerald-700' : 'text-gray-500'}>{text}</span>
    </div>
  );

  if (!token) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F9F9F7] p-6">
        <div className="noise-overlay" />
        <Card className="w-full max-w-md border-gray-100 shadow-lg">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-bold tracking-tight text-gray-900">
              Invalid reset link
            </CardTitle>
            <CardDescription className="text-base">
              This password reset link is missing a token or was copied incorrectly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/forgot-password">
              <Button className="w-full h-11 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white">
                Request a new reset link
              </Button>
            </Link>
            <Link to="/" className="block">
              <Button variant="outline" className="w-full h-11 rounded-full">
                Back to Sign In
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F9F9F7] p-6">
        <div className="noise-overlay" />
        <Card className="w-full max-w-md border-gray-100 shadow-lg">
          <CardHeader className="space-y-3 pb-6">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center shadow-lg">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
            </div>
            <div className="text-center">
              <CardTitle className="text-2xl font-bold tracking-tight text-gray-900">
                Password updated
              </CardTitle>
              <CardDescription className="text-base mt-1">
                You can now sign in with your new password.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/', { replace: true })}
              className="w-full h-11 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white"
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F9F9F7] p-6">
      <div className="noise-overlay" />
      <Card className="w-full max-w-md border-gray-100 shadow-lg">
        <CardHeader className="space-y-2 pb-6">
          <div className="text-center">
            <CardTitle className="text-2xl font-bold tracking-tight text-gray-900">
              Create a new password
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Choose a strong password you haven’t used before.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white pr-10"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div className="mt-2 space-y-1">
                <Rule ok={rules.hasMinLen} text="At least 8 characters" />
                <Rule ok={rules.hasLetter} text="Contains a letter (A–Z)" />
                <Rule ok={rules.hasNumber} text="Contains a number (0–9)" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white pr-10"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div className="mt-2">
                <Rule ok={rules.match} text="Passwords match" />
              </div>
            </div>

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-11 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white shadow-md hover:shadow-lg transition-all duration-300 font-medium disabled:opacity-60"
            >
              {loading ? 'Resetting…' : 'Reset Password'}
            </Button>

            <div className="text-xs text-gray-500 text-center">
              If this link is expired, request a new one from{' '}
              <Link to="/forgot-password" className="text-[#1A4D2E] font-medium hover:underline">
                Forgot password
              </Link>
              .
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-gray-600 hover:text-[#1A4D2E] transition-colors font-medium"
            >
              <ArrowLeft className="w-3 h-3 inline mr-1" />
              Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
